import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, LoaderCircle, RefreshCw } from "lucide-react";
import { Link, useParams } from "react-router";
import { Brand } from "../../components/Brand";
import { api, assertJoinResult, getApiErrorMessage } from "../../lib/api";
import { ui } from "../../lib/ui";
import {
  loadReconnectMetadata,
  loadRoomSession,
  saveRoomSession,
  type ReconnectMetadata,
  type StoredRoomSession,
} from "./roomSession";

const MeetingRuntime = lazy(() => import("./MeetingRuntime"));
const PrivateMeetingRuntime = lazy(() => import("../private-call/PrivateMeetingRuntime"));

async function reconnectSession(roomId: string, metadata: ReconnectMetadata, signal?: AbortSignal): Promise<StoredRoomSession> {
  const joined = assertJoinResult(await api.rooms.reconnect(roomId, signal));
  const session: StoredRoomSession = {
    result: { ...joined, room: { ...metadata.room, ...joined.room } },
    media: metadata.media,
  };
  saveRoomSession(session);
  return session;
}

export function MeetingPage() {
  const { roomId = "" } = useParams();
  const [session, setSession] = useState<StoredRoomSession | null>(() => loadRoomSession(roomId));
  const [metadata] = useState<ReconnectMetadata | null>(() => loadReconnectMetadata(roomId));
  const [recovering, setRecovering] = useState(() => !session && Boolean(metadata));
  const [recoveryError, setRecoveryError] = useState("");
	const recoveryPromise = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (session || !metadata || !roomId) return;
    const controller = new AbortController();
    reconnectSession(roomId, metadata, controller.signal).then(setSession).catch((error) => {
      if (!controller.signal.aborted) setRecoveryError(getApiErrorMessage(error));
    }).finally(() => { if (!controller.signal.aborted) setRecovering(false); });
    return () => controller.abort();
  }, [metadata, roomId, session]);

  const recover = useCallback((): Promise<void> => {
    if (recoveryPromise.current) return recoveryPromise.current;
    const latestMetadata = loadReconnectMetadata(roomId) ?? metadata;
    if (!latestMetadata) return Promise.reject(new Error(ui("Room admission is unavailable. Return to device check.", "Вход в комнату недоступен. Вернитесь к проверке устройств.")));
    setRecovering(true);
    setRecoveryError("");
    const pending = reconnectSession(roomId, latestMetadata).then(setSession).catch((error: unknown) => {
      setRecoveryError(getApiErrorMessage(error));
      throw error;
    }).finally(() => {
      recoveryPromise.current = null;
      setRecovering(false);
    });
    recoveryPromise.current = pending;
    return pending;
  }, [metadata, roomId]);

  if (!session) {
    if (recovering) return <main id="main-content" className="room-loading" role="status"><LoaderCircle className="spin" aria-hidden="true" /><strong>{ui("Recovering your room…", "Восстанавливаем комнату…")}</strong><span>{ui("Talk is requesting fresh, cookie-bound room credentials.", "OnlyChess запрашивает новый допуск в комнату.")}</span></main>;
    return <main id="main-content" className="room-gate"><Brand /><div><h1>{metadata ? ui("Room recovery paused.", "Восстановление комнаты приостановлено.") : ui("Join this room first.", "Сначала войдите в комнату.")}</h1><p>{recoveryError || (metadata ? ui("Talk could not recover this room. Retry or return to device check.", "OnlyChess не смог восстановить комнату. Повторите попытку или вернитесь к проверке устройств.") : ui("A fresh room admission is required before Talk can connect your media.", "Чтобы OnlyChess подключил звук и видео, нужно заново войти в комнату."))}</p><div className="button-row">{metadata ? <button className="button button--primary" type="button" onClick={() => void recover()}><RefreshCw aria-hidden="true" size={17} />{ui("Retry", "Повторить")}</button> : null}<Link className={`button ${metadata ? "button--secondary" : "button--primary"}`} to={`/r/${encodeURIComponent(roomId)}/prejoin`}><ArrowLeft aria-hidden="true" size={17} />{ui("Back to device check", "К проверке устройств")}</Link></div></div></main>;
  }

  const Runtime = session.result.room.mode === "private_1to1" ? PrivateMeetingRuntime : MeetingRuntime;
  const runtimeKey = session.result.room.mode === "private_1to1"
    ? `${session.result.participant.id}:${session.result.rtcBinding.backend}:${session.result.rtcBinding.epoch}`
    : session.result.participant.id;
  return <Suspense fallback={<div className="room-loading" role="status"><span className="join-spinner" /><strong>{ui("Preparing the room…", "Готовим комнату…")}</strong></div>}><Runtime key={runtimeKey} session={session} onRecover={recover} /></Suspense>;
}
